import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Plus, Camera, Loader2, Check } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function QuickExpense() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [method, setMethod] = useState("contanti");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reading, setReading] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setAmount(""); setCategory(""); setMethod("contanti"); setFile(null); };

  const onFile = async (f) => {
    setFile(f);
    if (!f) return;
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ai/extract-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.amount) setAmount(String(data.amount));
      if (data.category) setCategory(data.category);
      toast.success("Scontrino letto dall'AI");
    } catch { toast.error("Impossibile leggere lo scontrino"); }
    finally { setReading(false); }
  };

  const save = async () => {
    if (!amount || !category) return toast.error("Inserisci importo e categoria");
    setSaving(true);
    try {
      let receipt_path = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_path = data.path;
      }
      await api.post("/transactions", {
        kind: "spesa", category, amount: parseFloat(amount),
        date: today(), payment_method: method, receipt_path,
      });
      toast.success("Spesa registrata");
      reset(); setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    location.pathname.startsWith("/pos") ? null :    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="quick-expense-fab"
          className="fixed bottom-6 right-6 z-50 rounded-full h-14 px-6 shadow-lg hover:-translate-y-0.5 transition-transform">
          <Plus className="w-5 h-5 mr-1" /> Spesa
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="quick-expense-dialog">
        <DialogHeader><DialogTitle>Registra una spesa</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Importo (€)</Label>
            <Input type="number" inputMode="decimal" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="0,00"
              className="text-2xl h-14" data-testid="quick-expense-amount" autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Categoria</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="Fornitori, Utenze, Manutenzione…" data-testid="quick-expense-category" />
          </div>
          <div className="space-y-2">
            <Label>Metodo di pagamento</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="quick-expense-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contanti">Contanti</SelectItem>
                <SelectItem value="carta">Carta</SelectItem>
                <SelectItem value="bonifico">Bonifico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Foto scontrino (opzionale)</Label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              onChange={(e) => onFile(e.target.files?.[0] || null)} className="hidden" data-testid="quick-expense-file" />
            <Button type="button" variant="outline" className="w-full rounded-xl h-12"
              onClick={() => fileRef.current?.click()} disabled={reading} data-testid="quick-expense-photo-button">
              {reading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lettura AI…</>
                : file ? <><Check className="w-4 h-4 mr-2 text-[hsl(var(--success))]" /> {file.name.slice(0, 24)}</>
                    : <><Camera className="w-4 h-4 mr-2" /> Scatta o carica foto</>}
            </Button>
            <p className="text-xs text-muted-foreground">L'AI legge lo scontrino e compila importo e categoria.</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full w-full h-12" data-testid="quick-expense-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva spesa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
